import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { currentOrg, getOrgHeaders, API } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!currentOrg) { setStatus(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/payments/status`, { withCredentials: true, headers: getOrgHeaders() });
      setStatus(data);
    } catch (err) { console.error('Subscription fetch failed', err); setStatus(null); }
    finally { setLoading(false); }
  }, [currentOrg, API, getOrgHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh on window focus (user comes back to tab) + every 60s as a
  // safety net. This ensures SA flag changes propagate without forcing a reload.
  useEffect(() => {
    if (!currentOrg) return;
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    const intervalId = setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(intervalId);
    };
  }, [currentOrg, refresh]);

  const canInviteMore = () => {
    if (!status) return false;
    if (status.plan === 'free') return status.used_seats < status.free_plan_limit;
    if (status.plan === 'trial') return status.is_valid;
    if (status.plan === 'active') return status.used_seats < status.billable_users;
    return false;
  };

  // Backend sends `capabilities` on /payments/status. Default every cap to
  // true while loading so we never flash a "locked" state before status
  // arrives; individual components still render correctly once the real
  // plan is known.
  const caps = status?.capabilities || {
    contact_support: true, integrations_slack: true, integrations_teams: true,
    advanced_insights: true, burndown: true, csv_import: true, csv_export: true,
    docs: true, whiteboard: true, timeline: true, csv_import_export: true,
  };
  const hasCap = (k) => caps[k] !== false;
  // Distinguishes "feature locked by plan" vs "feature force-disabled by SA"
  // so the UpgradeLock card can render the right copy.
  const flagSource = (k) => {
    const adminFlag = caps?._flags?.[k];
    if (adminFlag === false) return 'admin_disabled';
    if (caps[k] === false) return 'plan_locked';
    return 'enabled';
  };
  const isFreePlan = status?.plan === 'free';
  const isInternal = status?.is_internal === true;
  const storage = status?.storage || null;
  // A paid subscription whose window has genuinely lapsed. Mirrors the
  // backend/BillingPage definition EXACTLY so the banner, the sidebar pill
  // and the Billing page never disagree:
  //   • plan === 'expired'  (SA-set lifecycle), OR
  //   • plan === 'active' with a past current_period_end.
  // NOTE: we deliberately do NOT use `is_valid` — isSubscriptionValid()
  // returns false for legitimate SA-set plan==='business' orgs, which would
  // wrongly flag a perfectly valid workspace as expired.
  const isExpired = !!status && !isInternal && (
    status.plan === 'expired'
    || (status.plan === 'active' && status.current_period_end && new Date(status.current_period_end) < new Date())
  );

  // Days until the paid window ends (trial → trial_ends_at, otherwise
  // current_period_end). Null for free / internal / already-expired orgs.
  // Drives the "your plan expires in N days" reminder banner.
  const periodEndRaw = status?.plan === 'trial' ? status?.trial_ends_at : status?.current_period_end;
  let expiresInDays = null;
  if (periodEndRaw && !isInternal && !isFreePlan && !isExpired) {
    const ms = new Date(periodEndRaw).getTime() - Date.now();
    expiresInDays = Math.ceil(ms / 86400000);
  }
  const isExpiringSoon = expiresInDays !== null && expiresInDays >= 0 && expiresInDays <= 7;
  const periodEnd = periodEndRaw || null;

  return (
    <SubscriptionContext.Provider value={{ status, loading, refresh, canInviteMore, caps, hasCap, flagSource, isFreePlan, isInternal, isExpired, isExpiringSoon, expiresInDays, periodEnd, storage }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => useContext(SubscriptionContext);
